/**
 * Plan-page "Baseline forecast calibration" card — the relocated (and
 * slimmed) successor of the old /calibration workspace tab.
 *
 * Rendered at every drill level of /plan with grain-appropriate depth:
 * - company/rep: headline metrics (12m rev · growth · contribution) + the
 *   combined actuals-history → baseline-forecast chart, plus a coverage
 *   footer (bucket count, under-evidenced count, honest failure count).
 * - bucket: the headlines + an evidence line (estimate/job counts) under
 *   them, a quiet seasonality strip under the chart, and the
 *   recalibration history ticks in the footer.
 *
 * Deliberately absent: the estimate win-rate curve AND the lead-time
 * pair. Both live on the open-quotes panel of the bucket drill — the
 * quote-funnel's curve IS ``forecasting_bucket_calibration.win_rate_curve``
 * and the leads describe the same estimate→booking→invoice conversion
 * mechanics that panel narrates.
 */
import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Area, ComposedChart, ReferenceLine, XAxis, YAxis } from "recharts";
import type {
  CalibrationCardData,
  CalibrationSeriesPoint,
} from "@shared-core/data/adapter";
import { fmtK, fmtM } from "@shared-core/domain/format-money";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { Card } from "../../primitives/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../../primitives/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../primitives/tooltip";

const PINK = "#f472b6";
const NEUTRAL = "#a3a3a3";
const AXIS = "#737373";

const FY_MONTH_LABELS = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

export function CalibrationCard({ data }: { data: CalibrationCardData }) {
  const d = data.detail;
  return (
    <Card className="p-4 space-y-3">
      <h3 className="text-sm font-semibold">Baseline forecast calibration</h3>

      {/* Tier 1 — headline metrics */}
      <div className="flex flex-wrap items-baseline gap-x-8 gap-y-1">
        <HeadlineStat
          label="12m rev"
          value={fmtMoneyAuto(data.twelveMonthRevGbp)}
          tooltip="Booked revenue over the last 12 months behind this calibration."
          warn={d?.lowEvidence}
          warnTooltip={
            d
              ? `Under-evidenced: only ${d.nOccasionsL12m} booking occasion${d.nOccasionsL12m === 1 ? "" : "s"} in the last 12 months.`
              : undefined
          }
        />
        <HeadlineStat
          label="Growth"
          value={
            data.growthPct == null
              ? "—"
              : `${data.growthPct > 0 ? "+" : ""}${data.growthPct.toFixed(1)}%`
          }
          tooltip="Year-over-year growth the calibration projects forward. Blank when there's no reliable growth term."
        />
        <HeadlineStat
          label="Contribution"
          value={`${data.contributionPct.toFixed(1)}%`}
          tooltip="Contribution as a percentage of revenue, measured over the calibration window."
        />
      </div>

      {/* Evidence line — how much data backs the calibration. Quiet, same
          voice as the footer. */}
      {d && (
        <p className="text-xs text-muted-foreground">
          Estimates{" "}
          <span className="text-foreground">
            {d.estimatesTotal} total · {d.estimatesL12m} L12M
          </span>
          <span className="mx-2">·</span>
          Jobs{" "}
          <span className="text-foreground">
            {d.jobsTotal} total · {d.jobsL12m} L12M
          </span>
        </p>
      )}

      {/* Headline chart — full actuals history, then the baseline forward */}
      <CalibrationHistoryChart series={data.series} />

      {d && <SeasonalityStrip pattern={d.seasonalPattern} />}

      <CardFooter data={data} />
    </Card>
  );
}

function fmtMoneyAuto(v: number): string {
  return Math.abs(v) >= 1_000_000 ? fmtM(v) : fmtK(v);
}

function HeadlineStat({
  label,
  value,
  tooltip,
  warn,
  warnTooltip,
}: {
  label: string;
  value: string;
  tooltip: string;
  warn?: boolean;
  warnTooltip?: string;
}) {
  return (
    <span className="text-sm">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-muted-foreground cursor-help border-b border-dotted border-muted-foreground/60">
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-left normal-case">
          {tooltip}
        </TooltipContent>
      </Tooltip>{" "}
      <span className="text-lg font-semibold">{value}</span>
      {warn && (
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle className="inline h-4 w-4 ml-1 -mt-0.5 text-amber-600" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-left normal-case">
            {warnTooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

// ── Combined history + baseline chart ────────────────────────────────

const historyChartConfig = {
  actualGbp: { label: "Actuals", color: NEUTRAL },
  baselineGbp: { label: "Baseline forecast", color: PINK },
} satisfies ChartConfig;

function CalibrationHistoryChart({
  series,
}: {
  series: CalibrationSeriesPoint[];
}) {
  // The "today" reference line sits on the meeting point of the two
  // series: the last actual month, which also carries the anchor baseline
  // value that makes the lines connect.
  const boundary = [...series]
    .reverse()
    .find((p) => p.actualGbp !== null)?.month;
  // Sparse x ticks: every 6th month keeps 48 points legible.
  const ticks = series.filter((_, i) => i % 6 === 0).map((p) => p.month);
  return (
    <ChartContainer
      config={historyChartConfig}
      className="aspect-auto h-36 w-full"
    >
      <ComposedChart
        data={series}
        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
      >
        <defs>
          <linearGradient id="calBaselineFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PINK} stopOpacity={0.18} />
            <stop offset="100%" stopColor={PINK} stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="calActualsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={NEUTRAL} stopOpacity={0.18} />
            <stop offset="100%" stopColor={NEUTRAL} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="month"
          ticks={ticks}
          tick={{ fontSize: 10, fill: AXIS }}
          axisLine={{ stroke: "#e5e5e5" }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fill: AXIS }}
          tickFormatter={(v: number) => fmtK(v)}
          axisLine={false}
          tickLine={false}
          width={52}
        />
        <ChartTooltip
          cursor={{ stroke: "#e5e5e5" }}
          content={
            <ChartTooltipContent
              formatter={(value, name) => {
                if (value == null) return null;
                const isBaseline = name === "baselineGbp";
                return (
                  <div className="flex w-full items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                      style={{ background: isBaseline ? PINK : NEUTRAL }}
                    />
                    <span className="text-muted-foreground">
                      {isBaseline ? "Baseline forecast" : "Actuals"}
                    </span>
                    <span className="text-foreground ml-auto font-mono font-medium tabular-nums">
                      {fmtK(Number(value))}
                    </span>
                  </div>
                );
              }}
            />
          }
        />
        {boundary && (
          <ReferenceLine
            x={boundary}
            stroke="#d4d4d4"
            strokeDasharray="3 3"
            label={{
              value: "today",
              position: "insideTopRight",
              fontSize: 9,
              fill: AXIS,
            }}
          />
        )}
        {/* One visual language for both series — a line with a soft fill.
            Colour + the dotted "today" divider carry the distinction. */}
        <Area
          type="monotone"
          dataKey="actualGbp"
          stroke={NEUTRAL}
          strokeWidth={2}
          fill="url(#calActualsFill)"
          dot={false}
          isAnimationActive={false}
        />
        <Area
          type="monotone"
          dataKey="baselineGbp"
          stroke={PINK}
          strokeWidth={2}
          fill="url(#calBaselineFill)"
          dot={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ChartContainer>
  );
}

// ── Seasonality strip (quiet, one row) ───────────────────────────────

/**
 * Deliberately understated: the seasonal shape is context, not a
 * headline — a row of small neutral bars anchored on a hairline 1.0×
 * average, tooltips carrying the per-month multiplier.
 */
function SeasonalityStrip({ pattern }: { pattern: number[] }) {
  // Bars encode ×-average intensity so a flat bucket LOOKS flat.
  const multipliers = pattern.map((p) => p * 12);
  const max = Math.max(...multipliers, 1.5);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>Seasonality</span>
      <div className="relative flex items-end gap-[3px] h-7">
        {/* 1.0× average reference */}
        <div
          className="absolute inset-x-0 border-t border-dashed border-muted-foreground/30"
          style={{ bottom: `${(1 / max) * 100}%` }}
        />
        {multipliers.map((m, i) => (
          <div
            key={i}
            className="flex h-full w-3 flex-col justify-end"
            title={`${FY_MONTH_LABELS[i]}: ${m.toFixed(2)}× average month`}
          >
            <div
              className="w-full rounded-[1px] bg-muted-foreground/30"
              style={{ height: `${Math.max(6, (m / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
      <span className="text-[10px]">Apr → Mar · vs average month</span>
    </div>
  );
}

// ── Footer — freshness + coverage / history ──────────────────────────

function CardFooter({ data }: { data: CalibrationCardData }) {
  const [showHistory, setShowHistory] = useState(false);
  const when = data.lastCalibratedAt
    ? formatDateTimeOrDash(data.lastCalibratedAt)
    : null;
  const d = data.detail;
  return (
    <div className="border-t border-border pt-2 text-xs text-muted-foreground space-y-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>{when ? `Last calibrated ${when}` : "Not yet calibrated"}</span>
        {data.bucketCount != null && <span>· {data.bucketCount} buckets</span>}
        {data.underEvidencedCount != null && data.underEvidencedCount > 0 && (
          <span className="text-amber-700">
            · {data.underEvidencedCount} under-evidenced
          </span>
        )}
        {data.failedBucketCount != null && (
          <span
            className={
              data.failedBucketCount === 0 ? "text-green-700" : "text-amber-700"
            }
          >
            · {data.failedBucketCount} failed calibration
          </span>
        )}
        {d && d.historyTicks.length > 0 && (
          <button
            type="button"
            className="text-xs font-normal text-muted-foreground hover:underline"
            onClick={() => setShowHistory((v) => !v)}
          >
            · {showHistory ? "hide" : "show"} history ({d.historyTicks.length}{" "}
            ticks)
          </button>
        )}
      </div>
      {d && showHistory && (
        <ul className="space-y-0.5">
          {d.historyTicks.map((t, i) => (
            <li key={i} className="border-l-2 border-border pl-2">
              {formatDateOrDash(t.at)} · {t.origin} · annual {fmtK(t.annualGbp)}{" "}
              <span
                className={
                  t.deltaGbp === 0
                    ? ""
                    : t.deltaGbp > 0
                      ? "text-green-700"
                      : "text-red-700"
                }
              >
                {t.deltaGbp === 0
                  ? "→ 0"
                  : `${t.deltaGbp > 0 ? "↗ +" : "↓ "}${fmtK(t.deltaGbp)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
